/**
 * Card Components - HELIX Professional Design System v9.2.1
 *
 * EXECUTIVE READABILITY PASS:
 * - Card title: 16px (executive-readable)
 * - Card description: 13px min (decision-grade text)
 * - Line height: 1.5 for body text readability
 *
 * Standard card: surface + border + radius.lg + shadow.card
 * Interactive card: adds hover shadow + optional selected state
 * Padding: 18-22px per Executive OS
 */

import * as React from 'react';
import { cn } from '@/lib/cn';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Make card interactive with hover effects */
  interactive?: boolean;
  /** Show selected state */
  selected?: boolean;
}

export const Card = React.forwardRef<HTMLDivElement, CardProps>(function Card(
  { className, interactive = false, selected = false, ...props },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn(
        // Base: surface + border + radius + shadow
        'rounded-lg border bg-card text-card-foreground',
        // Shadow: subtle card shadow from Executive OS
        'shadow-[0_1px_3px_rgba(0,0,0,0.04),0_1px_2px_rgba(0,0,0,0.06)]',
        // Border color
        selected
          ? 'border-[hsl(var(--helix-primary-medium))] bg-[hsl(var(--helix-primary-light))]'
          : 'border-border',
        // Interactive: hover shadow + cursor
        interactive && 'cursor-pointer transition-shadow duration-150',
        interactive && 'hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)]',
        className,
      )}
      {...props}
    />
  );
});

export const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  function CardHeader({ className, ...props }, ref) {
    return (
      <div
        ref={ref}
        // Padding: 18px per Operate mode
        className={cn('flex flex-col space-y-1.5 p-[18px] pb-0', className)}
        {...props}
      />
    );
  },
);

export const CardTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(function CardTitle({ className, ...props }, ref) {
  return (
    <h3
      ref={ref}
      // Typography: 16px card title (executive-readable)
      className={cn('text-[16px] font-semibold leading-[1.3] tracking-tight', className)}
      {...props}
    />
  );
});

export const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(function CardDescription({ className, ...props }, ref) {
  return (
    <p
      ref={ref}
      // Typography: 13px min decision text, 1.5 line-height
      className={cn('text-[13px] leading-[1.5] text-muted-foreground', className)}
      {...props}
    />
  );
});

export const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  function CardContent({ className, ...props }, ref) {
    return (
      <div
        ref={ref}
        // Padding: 18px horizontal, 14px top
        className={cn('px-[18px] pt-[14px] pb-[18px]', className)}
        {...props}
      />
    );
  },
);

export const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  function CardFooter({ className, ...props }, ref) {
    return (
      <div
        ref={ref}
        className={cn('flex items-center px-[18px] pb-[18px] pt-0', className)}
        {...props}
      />
    );
  },
);
